/**
 * PRD-21 §FR-12 — Type-safe subscription for the cross-cutting
 * ``task.progress`` realtime channel.
 *
 *   const progress = useTaskProgress(taskRunId)
 *   if (progress?.status === "running") {
 *     return <TaskProgressBar {...progress} />
 *   }
 *
 * Two combined signals:
 *   1. Realtime: subscribe to ``task.progress`` and filter by
 *      ``task_run_id`` — every transition the worker emits lands here
 *      with no polling.
 *   2. Late-join: fetch ``GET /api/task-runs/{id}`` once on mount so a
 *      page reload / new tab / reconnect doesn't lose state — the
 *      next broadcast covers everything after the fetch settles.
 *
 * The hook returns ``undefined`` while ``taskRunId`` is null/undefined
 * (the modal hasn't received the id from the action endpoint yet) AND
 * while the late-join fetch is in flight. Both states render as
 * "loading" in the consumer.
 */

import { taskRunsApi } from "@api/taskRuns"
import { useQuery } from "@tanstack/react-query"
import { useMemo } from "react"

import { useRealtimeEvent } from "./useRealtimeEvent"

import type { TaskRun } from "@/types"

// 60s default matches the repo-wide ``staleTime``. The realtime
// channel is the primary signal; the cached value just bridges the
// gap on reconnect.
const STALE_TIME_MS = 60_000

/** Cache keys of the TaskRun late-join read, for consumers that must
 *  invalidate it from an event the run itself did not broadcast. */
export const taskRunKeys = {
  all: ["task-runs"] as const,
  detail: (taskRunId: string | null | undefined) => [...taskRunKeys.all, taskRunId] as const,
}

export function useTaskProgress(taskRunId: string | null | undefined): TaskRun | undefined {
  const query = useQuery({
    queryKey: taskRunKeys.detail(taskRunId),
    queryFn: () => taskRunsApi.get(taskRunId as string),
    enabled: !!taskRunId,
    staleTime: STALE_TIME_MS,
    // PRD-21 §FR-12 mandates ``placeholderData: (prev) => prev`` so a
    // reconnect doesn't strip the visible progress while the fetch
    // is in flight.
    placeholderData: (prev) => prev,
  })

  // Realtime invalidation. Every ``task.progress`` event for the
  // bound task_run_id triggers a refetch. Coarse but bounded — one
  // GET per emit, capped by the worker's emit rate (one per phase
  // + one per OCR image, well under 10 emits per task).
  //
  // Why not write the payload into the cache directly? Because the
  // backend payload IS the schema we'd write — the GET round trip
  // adds < 50ms latency and keeps the cache canonical.
  const queryClient = useMemo(
    () => ({
      invalidateMatch: () => query.refetch(),
    }),
    [query],
  )

  // The socket may open AFTER the run started (cold tab, reconnect after a
  // drop). Every event emitted during that window is gone — the transport
  // has no replay. Re-reading the row on (re)connect closes the gap without
  // polling, which celery-progress.md §15 bans.
  useRealtimeEvent("realtime.connected", () => {
    if (taskRunId) void query.refetch()
  })

  useRealtimeEvent("task.progress", (payload) => {
    if (!taskRunId || payload.task_run_id !== taskRunId) return
    queryClient.invalidateMatch()
  })

  return query.data
}
